import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { container } from '@infrastructure/container';
import { Company } from '@domain/account/models/Company';
import { TUTORIAL_STEP_IDS, TutorialStepId } from './tutorialSteps';

export interface TutorialStep {
  id: TutorialStepId;
  timestamp?: number;
}

export type OverlayStepId = 'products' | 'inventory' | 'suppliers' | 'purchase-order';

interface TutorialState {
  completedSteps: TutorialStep[];
  currentStep: number;
  completed: boolean | null;
  /** Company pref: initial unlock/onboarding finished. Null until hydrated. */
  unlockDashboardCompleted: boolean | null;
  productOnboardingDismissed: boolean;
  productOnboardingRequested: boolean;
  warehouseOnboardingDismissed: boolean;
  warehouseOnboardingRequested: boolean;
  supplierOnboardingDismissed: boolean;
  supplierOnboardingRequested: boolean;
  purchaseOrderOnboardingDismissed: boolean;
  purchaseOrderOnboardingRequested: boolean;
  markStepCompleted: (stepId: TutorialStepId) => void;
  isStepCompleted: (stepId: TutorialStepId) => boolean;
  resetTutorial: () => void;
  isTutorialCompleted: () => boolean;
  setCurrentStep: (step: number) => void;
  completeTutorial: () => void;
  completeUnlockDashboard: () => void;
  openOverlay: (stepId: OverlayStepId) => void;
  dismissOverlay: (stepId: OverlayStepId) => void;
  isOverlayDismissed: (stepId: OverlayStepId) => boolean;
  isOverlayRequested: (stepId: OverlayStepId) => boolean;
  hydrateFromCompany: (company: Company) => void;
}

export const OVERLAY_STATE_KEYS = {
  products: {
    dismissed: 'productOnboardingDismissed',
    requested: 'productOnboardingRequested',
  },
  inventory: {
    dismissed: 'warehouseOnboardingDismissed',
    requested: 'warehouseOnboardingRequested',
  },
  suppliers: {
    dismissed: 'supplierOnboardingDismissed',
    requested: 'supplierOnboardingRequested',
  },
  'purchase-order': {
    dismissed: 'purchaseOrderOnboardingDismissed',
    requested: 'purchaseOrderOnboardingRequested',
  },
} as const satisfies Record<
  OverlayStepId,
  {
    dismissed:
    | 'productOnboardingDismissed'
    | 'warehouseOnboardingDismissed'
    | 'supplierOnboardingDismissed'
    | 'purchaseOrderOnboardingDismissed';
    requested:
    | 'productOnboardingRequested'
    | 'warehouseOnboardingRequested'
    | 'supplierOnboardingRequested'
    | 'purchaseOrderOnboardingRequested';
  }
>;

export const useTutorialStore = create<TutorialState>()(
  persist(
    (set, get) => ({
      completedSteps: [],
      currentStep: 0,
      completed: null,
      unlockDashboardCompleted: null,
      productOnboardingDismissed: false,
      productOnboardingRequested: false,
      warehouseOnboardingDismissed: false,
      warehouseOnboardingRequested: false,
      supplierOnboardingDismissed: false,
      supplierOnboardingRequested: false,
      purchaseOrderOnboardingDismissed: false,
      purchaseOrderOnboardingRequested: false,

      markStepCompleted: (stepId: TutorialStepId) => {
        set((state) => ({
          completedSteps: state.completedSteps.find((step) => step.id === stepId)
            ? state.completedSteps
            : [...state.completedSteps, { id: stepId, timestamp: Date.now() }],
        }));

        const { completedSteps, completed } = get();
        const allDone = TUTORIAL_STEP_IDS.every((id) =>
          completedSteps.some((step) => step.id === id),
        );
        if (allDone && completed !== true) {
          get().completeTutorial();
        }
      },

      isStepCompleted: (stepId: TutorialStepId) => {
        const step = get().completedSteps.find(s => s.id === stepId);
        return step !== undefined;
      },

      isTutorialCompleted: () => {
        return get().completed === true;
      },

      resetTutorial: () =>
        set({
          completedSteps: [],
          currentStep: 0,
          completed: null,
          unlockDashboardCompleted: null,
          productOnboardingDismissed: false,
          productOnboardingRequested: false,
          warehouseOnboardingDismissed: false,
          warehouseOnboardingRequested: false,
          supplierOnboardingDismissed: false,
          supplierOnboardingRequested: false,
          purchaseOrderOnboardingDismissed: false,
          purchaseOrderOnboardingRequested: false,
        }),

      /**
       * Company prefs are the source of truth. Re-read on every login —
       * including a company switch — rather than caching locally.
       * Missing prefs (legacy companies) default to completed/unlocked.
       */
      hydrateFromCompany: (company: Company) => {
        set({
          completed: company.settings?.tutorialCompleted ?? true,
          unlockDashboardCompleted: company.settings?.unlockDashboardCompleted ?? true,
        });
      },

      completeTutorial: () => {
        set({ completed: true });
        void container.companyRepository.updateSettings({ tutorialCompleted: true });
      },

      completeUnlockDashboard: () => {
        set({ unlockDashboardCompleted: true });
        void container.companyRepository.updateSettings({ unlockDashboardCompleted: true });
      },

      openOverlay: (stepId) => {
        const key = OVERLAY_STATE_KEYS[stepId].requested;
        set({ [key]: true } as Partial<TutorialState>);
      },

      dismissOverlay: (stepId) => {
        const { dismissed, requested } = OVERLAY_STATE_KEYS[stepId];
        set({ [dismissed]: true, [requested]: false } as Partial<TutorialState>);
      },

      isOverlayDismissed: (stepId) =>
        Boolean(get()[OVERLAY_STATE_KEYS[stepId].dismissed]),

      isOverlayRequested: (stepId) =>
        Boolean(get()[OVERLAY_STATE_KEYS[stepId].requested]),

      setCurrentStep: (step: number) => set({ currentStep: step > get().currentStep ? step : get().currentStep }),
    }),
    {
      name: 'tutorial-storage',
      // `completed` is deliberately absent: it lives on the company and is
      // hydrated at login, so a cached copy could only go stale.
      partialize: (state) => ({
        completedSteps: state.completedSteps,
        currentStep: state.currentStep,
        productOnboardingDismissed: state.productOnboardingDismissed,
        warehouseOnboardingDismissed: state.warehouseOnboardingDismissed,
        supplierOnboardingDismissed: state.supplierOnboardingDismissed,
        purchaseOrderOnboardingDismissed: state.purchaseOrderOnboardingDismissed,
      }),
      merge: (persisted, current) => {
        const state = (persisted ?? {}) as Partial<TutorialState>;
        const knownStepIds = new Set<string>(TUTORIAL_STEP_IDS);
        const completedSteps = (state.completedSteps ?? current.completedSteps).filter(
          (step) => knownStepIds.has(step.id),
        );
        return {
          ...current,
          ...state,
          completedSteps,
          // Ignore any company-pref flags left in storage by an older build.
          completed: null,
          unlockDashboardCompleted: null,
          productOnboardingDismissed: state.productOnboardingDismissed === true,
          productOnboardingRequested: false,
          warehouseOnboardingDismissed: state.warehouseOnboardingDismissed === true,
          warehouseOnboardingRequested: false,
          supplierOnboardingDismissed: state.supplierOnboardingDismissed === true,
          supplierOnboardingRequested: false,
          purchaseOrderOnboardingDismissed: state.purchaseOrderOnboardingDismissed === true,
          purchaseOrderOnboardingRequested: false,
        };
      },
    }
  )
);
